import { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import {
  IndianRupee,
  Search,
  CheckCircle2,
  Trash2,
  Phone,
  Download,
  RefreshCcw,
  ShoppingBag,
  Wallet,
  Pencil,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { downloadExcel } from "../../lib/excel";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const modeLabels = {
  cash: "Cash",
  upi: "UPI",
  cheque: "Cheque",
  bank: "Bank",
  other: "Other",
};

function AgeingPill({ bucket, amount }) {
  if (!amount || amount <= 0) {
    return (
      <div className="rounded p-1.5 text-center bg-[color:var(--surface-2)] text-[color:var(--muted)]">
        <div className="uppercase tracking-wide font-semibold text-[10px]">{bucket}d</div>
        <div className="tabular-nums text-[10px]">—</div>
      </div>
    );
  }
  const tone =
    bucket === "60-90" || bucket === "90+"
      ? "bg-[color:var(--primary)]/10 text-[color:var(--primary)]"
      : bucket === "30-60"
      ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
      : "bg-[color:var(--moss)]/15 text-[color:var(--moss)]";
  return (
    <div className={`rounded p-1.5 text-center ${tone}`}>
      <div className="uppercase tracking-wide font-semibold text-[10px]">{bucket}d</div>
      <div className="tabular-nums text-[10px]">{fmtINR(amount)}</div>
    </div>
  );
}

export default function Ledger() {
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [drillStoreId, setDrillStoreId] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  // Settle outstanding dialog
  const [settleStore, setSettleStore] = useState(null);
  const [settleAmount, setSettleAmount] = useState("");
  const [settleMode, setSettleMode] = useState("cash");
  const [settleRef, setSettleRef] = useState("");
  const [settleNote, setSettleNote] = useState("");
  const [settleSaving, setSettleSaving] = useState(false);
  // Edit existing payment
  const [editPay, setEditPay] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editMode, setEditMode] = useState("cash");
  const [editRef, setEditRef] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/ledger/summary"),
        api.get("/payments?pending=true"),
      ]);
      setSummary(s.data);
      setPendingPayments(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDrill = async (storeId) => {
    setDrillStoreId(storeId);
    setDrillData(null);
    try {
      const { data } = await api.get(`/stores/${storeId}/ledger`);
      setDrillData(data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const verifyPayment = async (pid) => {
    try {
      await api.patch(`/payments/${pid}/verify`);
      toast.success("Payment verified");
      load();
      if (drillStoreId) openDrill(drillStoreId);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const deletePayment = async (pid) => {
    if (!window.confirm("Delete this payment entry? This cannot be undone.")) return;
    try {
      await api.delete(`/payments/${pid}`);
      toast.success("Payment removed");
      load();
      if (drillStoreId) openDrill(drillStoreId);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const openSettle = (row) => {
    setSettleStore(row);
    setSettleAmount(String(row.outstanding));
    setSettleMode("cash");
    setSettleRef("");
    setSettleNote("");
  };

  const submitSettle = async () => {
    if (!settleStore) return;
    const amt = Number(settleAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setSettleSaving(true);
    try {
      await api.post("/payments", {
        store_id: settleStore.store_id,
        amount: amt,
        mode: settleMode,
        reference_no: settleRef || null,
        note: settleNote || `Admin settlement · ${settleStore.store_name}`,
      });
      toast.success(
        amt >= settleStore.outstanding
          ? `Outstanding cleared · ${settleStore.store_name}`
          : `₹${amt.toLocaleString("en-IN")} posted to ${settleStore.store_name}`,
      );
      setSettleStore(null);
      load();
      if (drillStoreId) openDrill(drillStoreId);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSettleSaving(false);
    }
  };

  const openEditPay = (p) => {
    setEditPay(p);
    setEditAmount(String(p.amount));
    setEditMode(p.mode || "cash");
    setEditRef(p.reference_no || "");
    setEditNote(p.note || "");
  };

  const submitEditPay = async () => {
    if (!editPay) return;
    const amt = Number(editAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setEditSaving(true);
    try {
      await api.patch(`/payments/${editPay.id}`, {
        amount: amt,
        mode: editMode,
        reference_no: editRef || null,
        note: editNote || null,
      });
      toast.success("Payment updated");
      setEditPay(null);
      load();
      if (drillStoreId) openDrill(drillStoreId);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setEditSaving(false);
    }
  };

  const exportExcel = () => {
    if (!summary) return;
    const today = new Date().toISOString().slice(0, 10);
    const unpaidRows = summary.rows.filter((r) => r.outstanding > 0);
    const paidRows = summary.rows.filter((r) => r.outstanding <= 0 && r.total_billed > 0);
    const detailHeaders = [
      "Store",
      "Type",
      "Owner",
      "Phone",
      "Billed",
      "Paid",
      "Outstanding",
      "0-30 days",
      "30-60 days",
      "60-90 days",
      "90+ days",
    ];
    const toRow = (r) => [
      r.store_name,
      r.store_type,
      r.owner_name || "",
      r.phone || "",
      r.total_billed,
      r.total_paid,
      r.outstanding,
      r.ageing["0-30"],
      r.ageing["30-60"],
      r.ageing["60-90"],
      r.ageing["90+"],
    ];
    const unpaidTotal = unpaidRows.reduce((a, b) => a + b.outstanding, 0);
    const paidBilled = paidRows.reduce((a, b) => a + b.total_billed, 0);
    downloadExcel(`GM_Kridam_Ledger_${today}.xlsx`, [
      {
        name: "Summary",
        headers: ["Field", "Value"],
        rows: [
          ["Generated at", new Date().toLocaleString("en-IN")],
          ["Total billed", summary.totals.billed],
          ["Total paid", summary.totals.paid],
          ["Total outstanding", summary.totals.outstanding],
          ["Unpaid stores", unpaidRows.length],
          ["Settled stores", paidRows.length],
          ["Aged 0-30", summary.totals.ageing["0-30"]],
          ["Aged 30-60", summary.totals.ageing["30-60"]],
          ["Aged 60-90", summary.totals.ageing["60-90"]],
          ["Aged 90+", summary.totals.ageing["90+"]],
        ],
      },
      {
        name: `Unpaid (${unpaidRows.length})`,
        headers: detailHeaders,
        rows: [
          ...unpaidRows.map(toRow),
          unpaidRows.length > 0
            ? ["TOTAL", "", "", "", "", "", unpaidTotal, "", "", "", ""]
            : null,
        ].filter(Boolean),
      },
      {
        name: `Paid (${paidRows.length})`,
        headers: detailHeaders,
        rows: [
          ...paidRows.map(toRow),
          paidRows.length > 0
            ? ["TOTAL", "", "", "", paidBilled, paidBilled, 0, "", "", "", ""]
            : null,
        ].filter(Boolean),
      },
    ]);
  };

  const baseRows = summary?.rows || [];
  const filterByQ = (rows) =>
    rows.filter((r) =>
      !q.trim()
        ? true
        : (r.store_name + " " + (r.owner_name || "")).toLowerCase().includes(q.toLowerCase()),
    );
  const unpaid = filterByQ(baseRows.filter((r) => r.outstanding > 0));
  const paid = filterByQ(baseRows.filter((r) => r.outstanding <= 0 && r.total_billed > 0));

  return (
    <div className="space-y-5" data-testid="admin-ledger-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[color:var(--muted)]">Receivables</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Outstanding ledger
          </h1>
          <p className="text-[color:var(--muted)] mt-1">
            Per-store dues, ageing, and payment verification.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={load}
            disabled={loading}
            variant="outline"
            data-testid="ledger-refresh-btn"
          >
            <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={exportExcel}
            disabled={!summary}
            variant="outline"
            data-testid="ledger-export-btn"
          >
            <Download className="h-4 w-4 mr-1" /> Export
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Outstanding" value={fmtINR(summary.totals.outstanding)} tone="primary" />
          <KpiCard label="Billed" value={fmtINR(summary.totals.billed)} tone="ink" />
          <KpiCard label="Paid" value={fmtINR(summary.totals.paid)} tone="moss" />
          <KpiCard
            label="Pending verification"
            value={pendingPayments.length}
            sub={pendingPayments.length > 0 ? "Tap a store to review" : "All caught up"}
            tone="mustard"
          />
        </div>
      )}

      {pendingPayments.length > 0 && (
        <div
          className="rounded-xl border border-[color:var(--mustard)]/40 bg-[color:var(--mustard)]/5 p-4"
          data-testid="pending-payments-card"
        >
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="overline text-[#9A742E]">Action required</p>
              <h3 className="font-display text-lg font-bold">
                {pendingPayments.length} payment{pendingPayments.length === 1 ? "" : "s"} awaiting verification
              </h3>
            </div>
          </div>
          <div className="space-y-2">
            {pendingPayments.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3 bg-white rounded-lg border border-[color:var(--border)] p-3"
                data-testid={`pending-payment-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.store_name}</div>
                  <div className="text-xs text-[color:var(--muted)] truncate">
                    {p.salesman_name} · {modeLabels[p.mode] || p.mode}
                    {p.reference_no ? ` · ${p.reference_no}` : ""}
                    {p.note ? ` · ${p.note}` : ""}
                  </div>
                  <div className="text-[10px] text-[color:var(--muted)]">
                    {new Date(p.collected_at).toLocaleString("en-IN")}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-bold tabular-nums">{fmtINR(p.amount)}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => verifyPayment(p.id)}
                    className="bg-[color:var(--moss)] hover:bg-[#3c4e41] text-white h-9"
                    data-testid={`verify-payment-${p.id}`}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Verify
                  </Button>
                  <button
                    onClick={() => deletePayment(p.id)}
                    className="p-2 text-[color:var(--muted)] hover:text-[color:var(--primary)]"
                    data-testid={`delete-payment-${p.id}`}
                    aria-label="Delete payment"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Input
        placeholder="Search store…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="h-11 max-w-md"
        data-testid="ledger-search-input"
      />

      <Tabs defaultValue="unpaid" data-testid="ledger-tabs">
        <TabsList>
          <TabsTrigger value="unpaid" data-testid="tab-unpaid">
            Unpaid ({unpaid.length})
          </TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid">
            Paid ({paid.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="unpaid">
          <LedgerTable rows={unpaid} onDrill={openDrill} onSettle={openSettle} emptyText="No outstanding stores 🎉" showSettle />
        </TabsContent>
        <TabsContent value="paid">
          <LedgerTable rows={paid} onDrill={openDrill} emptyText="No fully-settled stores yet." />
        </TabsContent>
      </Tabs>

      <Dialog open={!!drillStoreId} onOpenChange={(o) => !o && setDrillStoreId(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="drill-dialog">
          <DialogHeader>
            <DialogTitle>
              {drillData ? drillData.store.name : "Loading…"}
            </DialogTitle>
          </DialogHeader>
          {drillData && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <KpiCard label="Billed" value={fmtINR(drillData.total_billed)} tone="ink" />
                <KpiCard label="Paid" value={fmtINR(drillData.total_paid)} tone="moss" />
                <KpiCard
                  label="Outstanding"
                  value={fmtINR(drillData.outstanding)}
                  tone={drillData.outstanding > 0 ? "primary" : "moss"}
                />
              </div>

              <div>
                <h3 className="font-display font-bold mb-2 flex items-center gap-2">
                  <Wallet className="h-4 w-4" /> Payments ({drillData.payments.length})
                </h3>
                <div className="rounded-lg border border-[color:var(--border)] divide-y">
                  {drillData.payments.length === 0 && (
                    <div className="p-4 text-sm text-[color:var(--muted)] text-center">
                      No payments recorded yet.
                    </div>
                  )}
                  {drillData.payments.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 p-3"
                      data-testid={`drill-payment-${p.id}`}
                    >
                      <div className="flex-1 min-w-0 text-sm">
                        <div className="font-medium">
                          {fmtINR(p.amount)}{" "}
                          <span className="text-xs text-[color:var(--muted)] font-normal">
                            · {modeLabels[p.mode]} {p.reference_no ? `· ${p.reference_no}` : ""}
                          </span>
                        </div>
                        <div className="text-xs text-[color:var(--muted)]">
                          {p.salesman_name} · {new Date(p.collected_at).toLocaleString("en-IN")}
                          {p.note ? ` · ${p.note}` : ""}
                          {p.auto ? " · auto (cash on delivery)" : ""}
                        </div>
                      </div>
                      {p.verified ? (
                        <span className="text-[10px] px-2 py-1 rounded-full bg-[color:var(--moss)]/15 text-[color:var(--moss)] uppercase tracking-wide font-semibold shrink-0">
                          Verified
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => verifyPayment(p.id)}
                          className="bg-[color:var(--moss)] hover:bg-[#3c4e41] text-white h-8"
                          data-testid={`drill-verify-${p.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Verify
                        </Button>
                      )}
                      <button
                        onClick={() => openEditPay(p)}
                        className="p-1.5 text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                        aria-label="Edit payment"
                        data-testid={`drill-edit-${p.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deletePayment(p.id)}
                        className="p-1.5 text-[color:var(--muted)] hover:text-[color:var(--primary)]"
                        aria-label="Delete payment"
                        data-testid={`drill-delete-${p.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-display font-bold mb-2 flex items-center gap-2">
                  <ShoppingBag className="h-4 w-4" /> Orders ({drillData.orders.length})
                </h3>
                <div className="rounded-lg border border-[color:var(--border)] divide-y max-h-72 overflow-y-auto">
                  {drillData.orders.length === 0 && (
                    <div className="p-4 text-sm text-[color:var(--muted)] text-center">
                      No orders yet.
                    </div>
                  )}
                  {drillData.orders.map((o) => (
                    <div key={o.id} className="flex items-center gap-3 p-3 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium tabular-nums">{fmtINR(o.total)}</div>
                        <div className="text-xs text-[color:var(--muted)]">
                          {new Date(o.created_at).toLocaleString("en-IN")} · {o.items.length} items
                        </div>
                      </div>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold ${
                          o.payment_status === "paid"
                            ? "bg-[color:var(--moss)]/15 text-[color:var(--moss)]"
                            : "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                        }`}
                      >
                        {o.payment_status === "paid" ? "Paid" : "Credit"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settle outstanding dialog */}
      <Dialog open={!!settleStore} onOpenChange={(o) => !o && setSettleStore(null)}>
        <DialogContent className="max-w-md" data-testid="settle-dialog">
          <DialogHeader>
            <DialogTitle>
              Close outstanding{settleStore ? ` · ${settleStore.store_name}` : ""}
            </DialogTitle>
          </DialogHeader>
          {settleStore && (
            <div className="space-y-3">
              <div className="rounded-lg bg-[color:var(--mustard)]/10 border border-[color:var(--mustard)]/30 p-3 text-sm">
                Current outstanding:{" "}
                <b className="text-[color:var(--ink)]">{fmtINR(settleStore.outstanding)}</b>
              </div>
              <div>
                <Label>Amount received (you can alter)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={settleAmount}
                  onChange={(e) => setSettleAmount(e.target.value)}
                  className="h-12 font-mono text-lg"
                  data-testid="settle-amount-input"
                  autoFocus
                />
                {Number(settleAmount) > 0 && Number(settleAmount) < settleStore.outstanding && (
                  <div className="text-xs text-[#9A742E] mt-1">
                    Partial settlement — {fmtINR(settleStore.outstanding - Number(settleAmount))} will remain outstanding.
                  </div>
                )}
                {Number(settleAmount) >= settleStore.outstanding && (
                  <div className="text-xs text-[color:var(--moss)] mt-1">
                    This will fully close the ledger for this store.
                  </div>
                )}
              </div>
              <div>
                <Label>Mode</Label>
                <Select value={settleMode} onValueChange={setSettleMode}>
                  <SelectTrigger className="h-12" data-testid="settle-mode-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="bank">Bank transfer</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(settleMode === "upi" || settleMode === "cheque" || settleMode === "bank") && (
                <div>
                  <Label>Reference / cheque no.</Label>
                  <Input
                    value={settleRef}
                    onChange={(e) => setSettleRef(e.target.value)}
                    className="h-12"
                    data-testid="settle-ref-input"
                  />
                </div>
              )}
              <div>
                <Label>Note (optional)</Label>
                <Textarea
                  value={settleNote}
                  onChange={(e) => setSettleNote(e.target.value)}
                  rows={2}
                  data-testid="settle-note-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSettleStore(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={submitSettle}
                  disabled={settleSaving || !settleAmount || Number(settleAmount) <= 0}
                  className="bg-[color:var(--moss)] hover:bg-[#3c4e41] text-white"
                  data-testid="settle-submit"
                >
                  {settleSaving
                    ? "Saving…"
                    : Number(settleAmount) >= (settleStore?.outstanding || 0)
                    ? "Close outstanding"
                    : "Post partial"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit / alter payment dialog */}
      <Dialog open={!!editPay} onOpenChange={(o) => !o && setEditPay(null)}>
        <DialogContent className="max-w-md" data-testid="edit-payment-dialog">
          <DialogHeader>
            <DialogTitle>
              Alter payment{editPay ? ` · ${editPay.store_name}` : ""}
            </DialogTitle>
          </DialogHeader>
          {editPay && (
            <div className="space-y-3">
              <div className="text-xs text-[color:var(--muted)]">
                Collected{" "}
                {new Date(editPay.collected_at).toLocaleString("en-IN")} ·{" "}
                {editPay.salesman_name || "admin"}
              </div>
              <div>
                <Label>Amount (INR)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  className="h-12 font-mono text-lg"
                  data-testid="edit-payment-amount"
                  autoFocus
                />
              </div>
              <div>
                <Label>Mode</Label>
                <Select value={editMode} onValueChange={setEditMode}>
                  <SelectTrigger className="h-12" data-testid="edit-payment-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="bank">Bank transfer</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(editMode === "upi" || editMode === "cheque" || editMode === "bank") && (
                <div>
                  <Label>Reference / cheque no.</Label>
                  <Input
                    value={editRef}
                    onChange={(e) => setEditRef(e.target.value)}
                    className="h-12"
                    data-testid="edit-payment-ref"
                  />
                </div>
              )}
              <div>
                <Label>Note (optional)</Label>
                <Textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  rows={2}
                  data-testid="edit-payment-note"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditPay(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={submitEditPay}
                  disabled={editSaving || !editAmount || Number(editAmount) <= 0}
                  className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                  data-testid="edit-payment-save"
                >
                  {editSaving ? "Saving…" : "Save changes"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, sub, tone = "primary" }) {
  const toneMap = {
    primary: "bg-[color:var(--primary)]/10 text-[color:var(--primary)]",
    moss: "bg-[color:var(--moss)]/10 text-[color:var(--moss)]",
    mustard: "bg-[color:var(--mustard)]/15 text-[#9A742E]",
    ink: "bg-[color:var(--ink)]/5 text-[color:var(--ink)]",
  };
  return (
    <div className="rounded-xl border border-[color:var(--border)] bg-white p-4 ink-shadow">
      <div className="flex items-center justify-between">
        <span className="overline text-[color:var(--muted)]">{label}</span>
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${toneMap[tone]}`}>
          <IndianRupee className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-2 font-display text-2xl font-bold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-[color:var(--muted)] mt-1">{sub}</div>}
    </div>
  );
}

function LedgerTable({ rows, onDrill, onSettle, showSettle = false, emptyText }) {
  return (
    <div className="rounded-xl border border-[color:var(--border)] bg-white ink-shadow overflow-hidden mt-2">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead className="bg-[color:var(--surface-2)] text-[color:var(--muted)] uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Store</th>
              <th className="px-4 py-3 text-right">Billed</th>
              <th className="px-4 py-3 text-right">Paid</th>
              <th className="px-4 py-3 text-right">Outstanding</th>
              <th className="px-4 py-3 text-left hidden lg:table-cell">Ageing</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-[color:var(--muted)]">
                  {emptyText}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.store_id}
                className="border-t border-[color:var(--border)] hover:bg-[color:var(--surface-2)]/40"
                data-testid={`ledger-row-${r.store_id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-medium">{r.store_name}</div>
                  <div className="text-xs text-[color:var(--muted)] flex items-center gap-2">
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wide font-semibold ${
                        r.store_type === "wholesale"
                          ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                          : "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
                      }`}
                    >
                      {r.store_type}
                    </span>
                    {r.owner_name ? <span className="truncate">{r.owner_name}</span> : null}
                    {r.phone ? (
                      <a
                        href={`tel:${r.phone}`}
                        className="flex items-center gap-1 text-[color:var(--primary)]"
                      >
                        <Phone className="h-3 w-3" /> {r.phone}
                      </a>
                    ) : null}
                  </div>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtINR(r.total_billed)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[color:var(--moss)]">
                  {fmtINR(r.total_paid)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-bold">
                  {r.outstanding > 0 ? (
                    <span className="text-[color:var(--primary)]">{fmtINR(r.outstanding)}</span>
                  ) : (
                    <span className="text-[color:var(--moss)]">{fmtINR(0)}</span>
                  )}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <div className="grid grid-cols-4 gap-1 min-w-[260px]">
                    {["0-30", "30-60", "60-90", "90+"].map((b) => (
                      <AgeingPill key={b} bucket={b} amount={r.ageing[b]} />
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center gap-1 justify-end">
                    {showSettle && r.outstanding > 0 && (
                      <Button
                        size="sm"
                        onClick={() => onSettle?.(r)}
                        className="bg-[color:var(--moss)] hover:bg-[#3c4e41] text-white h-8"
                        data-testid={`ledger-settle-${r.store_id}`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Settle
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDrill(r.store_id)}
                      data-testid={`ledger-drill-${r.store_id}`}
                    >
                      <Search className="h-4 w-4 mr-1" /> Details
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
